import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { useLanguage } from '../components/Layout/LanguageContext';
import { blogPosts } from '../data/blogPosts';

const PostSection = styled.section`
  padding: 6rem 2rem;
  background: var(--background-color);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 780px;
  margin: 0 auto;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 2rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: var(--background-light);
    transform: translateX(-4px);
  }
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const CategoryBadge = styled.span`
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  color: white;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.3rem 0.85rem;
  border-radius: 999px;
`;

const PostDate = styled.span`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const PostTitle = styled.h1`
  font-size: 2.6rem;
  font-weight: 700;
  color: var(--primary-color);
  line-height: 1.25;
  margin: 0 0 2rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  max-height: 420px;
  object-fit: cover;
  border-radius: 20px;
  margin-bottom: 2rem;
  display: block;
  box-shadow: 0 12px 40px rgba(0, 51, 102, 0.12);
`;

const Article = styled.article`
  background: var(--background-light);
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 12px 40px rgba(0, 51, 102, 0.1);
  border: 1px solid var(--background-dark);

  @media (max-width: 768px) {
    padding: 1.75rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1.05rem;
  line-height: 1.75;
  color: var(--text-color);
  margin: 0 0 1.4rem;
  white-space: pre-line;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 2.5rem;
  padding-top: 1.75rem;
  border-top: 1px solid var(--background-dark);
`;

const Tag = styled.span`
  background: var(--background-color);
  color: var(--secondary-color);
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  border: 1px solid var(--background-dark);
`;

const NotFound = styled.div`
  text-align: center;
  padding: 4rem 1rem;
`;

const NotFoundTitle = styled.h1`
  font-size: 2rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const blogPostUi = {
  de: { back: 'Zurück zum Blog', notFound: 'Beitrag nicht gefunden.' },
  en: { back: 'Back to the blog', notFound: 'Post not found.' },
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const ui = blogPostUi[language];
  const locale = language === 'de' ? 'de-DE' : 'en-GB';

  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <PostSection>
        <Container>
          <NotFound>
            <NotFoundTitle>{ui.notFound}</NotFoundTitle>
            <BackButton to="/blog">
              <FaArrowLeft />
              {ui.back}
            </BackButton>
          </NotFound>
        </Container>
      </PostSection>
    );
  }

  const c = post[language];

  return (
    <PostSection>
      <Container>
        <BackButton to="/blog">
          <FaArrowLeft />
          {ui.back}
        </BackButton>

        <PostMeta>
          <CategoryBadge>{post.category[language]}</CategoryBadge>
          <PostDate>
            {new Date(post.date).toLocaleDateString(locale, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </PostDate>
        </PostMeta>

        <PostTitle>{c.title}</PostTitle>

        {post.image && <HeroImage src={post.image} alt={c.title} />}

        <Article
          as={motion.article}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {c.body.map((paragraph, index) => (
            <Paragraph key={index}>{paragraph}</Paragraph>
          ))}

          <TagList>
            {post.tags.map((tag) => (
              <Tag key={tag}>#{tag}</Tag>
            ))}
          </TagList>
        </Article>
      </Container>
    </PostSection>
  );
};

export default BlogPost;
